// apps/merchant-v3/src/store/prereq.ts
//
// Phase 6 Wave 2 Task 04 — Zustand store for cross-phase BE prereq
// runtime probe results. Set by `checkBEPrereqs()` (lib/api/prereqCheck);
// read by `<BEPrereqsBanner />` and any feature-flag check that wants
// to disable a UI element when its backing endpoint is missing.
//
//  lenient gate: P6 ships ahead of P1/P3/P5 patches; this
// store lets us gracefully degrade individual features rather than
// blocking the whole portal.
//
// vg-binding: PLAN:task-04
// vg-binding: P6.D-235
// vg-binding: P6.D-229
// vg-binding: P6.D-230
// vg-binding: P6.D-232
// vg-binding: P6.D-237

import { create } from 'zustand';

export type PrereqId =
  | 'p1_loyalty_tier'
  | 'p3_fx_rates'
  | 'p3_orders_pay'
  | 'p5_sync_logs';

export type PrereqState = 'unknown' | 'present' | 'missing' | 'inconclusive';

export interface PrereqStore {
  /** True when at least one prereq probed back as `missing`. Drives the
   *  yellow `<BEPrereqsBanner />`. */
  bePrereqsIncomplete: boolean;
  /** Per-prereq probe state. Useful for granular feature gating
   *  (e.g. hide loyalty tier badge when `p1_loyalty_tier === 'missing'`). */
  probes: Record<PrereqId, PrereqState>;
  /** ISO timestamp of last `checkBEPrereqs()` run. */
  lastCheckedAt: string | null;

  setProbe: (id: PrereqId, state: PrereqState) => void;
  setIncomplete: (v: boolean) => void;
  setLastCheckedAt: (iso: string) => void;
  reset: () => void;
}

const INITIAL_PROBES: Record<PrereqId, PrereqState> = {
  p1_loyalty_tier: 'unknown',
  p3_fx_rates: 'unknown',
  p3_orders_pay: 'unknown',
  p5_sync_logs: 'unknown',
};

export const usePrereqStore = create<PrereqStore>((set) => ({
  bePrereqsIncomplete: false,
  probes: { ...INITIAL_PROBES },
  lastCheckedAt: null,

  setProbe: (id, state) =>
    set((s) => ({ probes: { ...s.probes, [id]: state } })),
  setIncomplete: (v) => set({ bePrereqsIncomplete: v }),
  setLastCheckedAt: (iso) => set({ lastCheckedAt: iso }),
  reset: () =>
    set({
      bePrereqsIncomplete: false,
      probes: { ...INITIAL_PROBES },
      lastCheckedAt: null,
    }),
}));

/** Selector — convenience hook for `<BEPrereqsBanner />`. */
export function useBEPrereqsIncomplete(): boolean {
  return usePrereqStore((s) => s.bePrereqsIncomplete);
}
